import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  alpha,
  useTheme,
  Button,
  Grid,
} from "@mui/material";
import { getStyles } from "../index";

import GenerateIcon from "@mui/icons-material/AutoAwesome";
import SegmentIcon from "@mui/icons-material/Segment";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";

import EditIcon from "@mui/icons-material/Edit";
import RubricsDialog from "./RubricsDialog";

const RubricDetails = ({ theme, styles, user }) => {
  const [rubric, setRubric] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRubric, setEditingRubric] = useState(null);

  const { id } = useParams();

  useEffect(() => {
    const fetchRubric = async () => {
      try {
        const rubricDoc = await getDoc(doc(db, "rubrics", id));
        if (rubricDoc.exists()) {
          setRubric({ id: rubricDoc.id, ...rubricDoc.data() });
        }
      } catch (error) {
        console.error("Error fetching rubric:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRubric();
  }, [id]);

  const refreshRubric = async () => {
    try {
      const rubricDoc = await getDoc(doc(db, "rubrics", id));
      if (rubricDoc.exists()) {
        setRubric({ id: rubricDoc.id, ...rubricDoc.data() });
      }
    } catch (error) {
      console.error("Error refreshing rubric:", error);
    }
  };

  const getGradeColor = (grade) => {
    const gradeColors = {
      A: "#2E7D32",
      C: "#FFA000",
      E: "#D32F2F",
      F: "#757575",
    };

    switch (grade) {
      case "A":
        return theme.palette.mode === "dark"
          ? "#15202b"
          : alpha(gradeColors.A, 0.1);
      case "C":
        return theme.palette.mode === "dark"
          ? "#15202b"
          : alpha(gradeColors.C, 0.1);
      case "E":
        return theme.palette.mode === "dark"
          ? "#15202b"
          : alpha(gradeColors.E, 0.1);
      case "F":
        return theme.palette.mode === "dark"
          ? "#15202b"
          : alpha(gradeColors.F, 0.1);
      default:
        return theme.palette.grey[100];
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!rubric) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography variant="h5">Bedömningsmatris hittades inte</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...styles.container }}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          <Button
            variant="purp"
            sx={{ ...styles.tightButton }}
            startIcon={<AddIcon />}
            component={Link}
            to={`/app/uppsats/ny`}
          >
            Ny bedömning
          </Button>

          {(rubric.userId === user.uid || rubric.sharedTo === user.uid) && (
            <Button
              variant="purp"
              sx={{ ...styles.tightButton }}
              startIcon={<EditIcon />}
              onClick={() => setEditingRubric(rubric)}
            >
              Redigera
            </Button>
          )}

          <Button
            variant="purp"
            sx={{ ...styles.tightButton }}
            component={Link}
            startIcon={<SegmentIcon />}
            to="/app/uppsatser"
            sx={{ display: "none" }}
          >
            Dina bedömningar
          </Button>
        </Box>

        <Box sx={{ mt: 8, mb: 3, display: "block", width: "100%" }}>
          <Typography
            variant="h1"
            sx={{
              ...styles.newH1,
              mt: { xs: 0, md: "inherit" },
              width: "100%",
            }}
          >
            {rubric.name}
          </Typography>
        </Box>

        {/* Criteria Section - Grid Layout matching Rubrics.js */}
        <Grid container spacing={4} sx={{ mb: 3 }}>
          {rubric.criteria.map((criterion, criteriaIndex) => (
            <Grid item xs={12} sm={12} md={6} key={criteriaIndex}>
              <Paper
                elevation={3}
                sx={{
                  ...styles.freshBox,
                  p: 3,
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                  backgroundImage: "none",
                  position: "relative",
                  height: "100%",
                }}
              >
                {/* Header with criterion name */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 3.5,
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "600", minWidth: "120px" }}
                  >
                    Kriterium {criteriaIndex + 1}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.95rem", md: "1.1rem" },
                      flex: 1,
                    }}
                  >
                    {criterion.name}
                  </Typography>
                </Box>

                {/* Descriptors in compact format */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {criterion.descriptors
                    .sort((a, b) => {
                      const grades = { A: 0, C: 1, E: 2, F: 3 };
                      return grades[a.grade] - grades[b.grade];
                    })
                    .map((descriptor, descriptorIndex) => (
                      <Box
                        key={descriptorIndex}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          p: 2,
                          backgroundColor: getGradeColor(descriptor.grade),
                          borderRadius: 4,
                          alignItems: "center", // Changed from "flex-start"
                        }}
                      >
                        {/* Grade Chip */}
                        <Chip
                          label={descriptor.grade}
                          size="small"
                          sx={{
                            fontWeight: "700",
                            minWidth: "40px",
                            minHeight: "40px",
                            fontSize: "1.2rem",
                            backgroundColor: (() => {
                              const gradeColors = {
                                A: "#2E7D32",
                                C: "#FFA000",
                                E: "#D32F2F",
                                F: "#757575",
                              };
                              return (
                                gradeColors[descriptor.grade] || gradeColors.F
                              );
                            })(),
                            color: "white",
                            border: `2px solid ${(() => {
                              const gradeColors = {
                                A: "#2E7D32",
                                C: "#FFA000",
                                E: "#D32F2F",
                                F: "#757575",
                              };
                              return (
                                gradeColors[descriptor.grade] || gradeColors.F
                              );
                            })()}`,
                          }}
                        />
                        {/* Description Text */}
                        <Typography
                          sx={{
                            flex: 1,

                            p: 1.5,
                            borderRadius: 3,
                          }}
                        >
                          {descriptor.text}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="purp"
          startIcon={<AddIcon />}
          component={Link}
          to={`/app/uppsats/ny`}
          sx={{ mb: 8 }}
        >
          Ny bedömning
        </Button>
      </Box>
      <RubricsDialog
        open={Boolean(editingRubric)}
        onClose={() => {
          setEditingRubric(null);
          refreshRubric();
        }}
        user={user}
        initialData={editingRubric}
        isEditing={true}
      />
    </Box>
  );
};

export default RubricDetails;
